import React, { useMemo } from "react";
import { useDashboardStats } from "../hooks/useDashboardStats";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DollarSign,
  CheckCircle2,
  Users2,
  AlertCircle,
  TrendingUp,
  FileText,
  MessageSquare,
  Wallet,
} from "lucide-react";

const COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

function StatCard({ icon, label, value, sub, color = "indigo" }) {
  const colorMap = {
    indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
    rose: "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400",
    violet: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
    cyan: "bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-400",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 dark:text-slate-100">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${colorMap[color] || colorMap.indigo}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function HealthAlert({ label, value, color = "rose" }) {
  const colorMap = {
    rose: "bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-400",
    amber: "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-400",
  };
  if (!value || value === 0) return null;
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${colorMap[color]}`}>
      <AlertCircle size={16} />
      <span className="text-sm font-medium">{label}: {value}</span>
    </div>
  );
}

export default function AdminMarketplaceDashboard() {
  const { data, loading, error, refetch } = useDashboardStats();

  const stats = data?.success ? data : null;
  const overview = stats?.overview || {};
  const finance = stats?.finance || {};
  const collaborations = stats?.collaborations || {};
  const offers = stats?.offers || {};
  const commissions = stats?.commissions || {};
  const invoices = stats?.invoices || {};
  const health = stats?.health || {};
  const charts = stats?.charts || {};

  const collaborationStatusData = useMemo(() => {
    if (!charts.collaboration_status) return [];
    return charts.collaboration_status.map((item) => ({
      name: item.label,
      value: item.value,
    }));
  }, [charts.collaboration_status]);

  const dailyActivityData = useMemo(() => {
    if (!charts.daily_activity) return [];
    return charts.daily_activity.map((item) => ({
      date: item.date,
      label: item.label,
      offers: item.offers || 0,
      collaborations: item.collaborations || 0,
      messages: item.messages || 0,
      transfers: item.transfers || 0,
    }));
  }, [charts.daily_activity]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin dark:border-indigo-500/20 dark:border-t-indigo-400" />
            <p className="text-sm text-slate-500 dark:text-slate-500">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center dark:bg-rose-500/10 dark:border-rose-500/30">
          <AlertCircle size={32} className="mx-auto text-rose-500 mb-2 dark:text-rose-400" />
          <p className="text-rose-700 font-medium dark:text-rose-400">Failed to load dashboard</p>
          <p className="text-sm text-rose-500 mt-1 dark:text-rose-400">{error.detail || error.message}</p>
          <button
            onClick={refetch}
            className="mt-3 px-4 py-2 bg-rose-600 text-white rounded-lg text-sm hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Marketplace Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5 dark:text-slate-500">
            Overview of your marketplace activity, finances, and health.
          </p>
        </div>
        {stats?.last_updated_at && (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Last updated: {new Date(stats.last_updated_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Health Alerts */}
      {health.requires_attention && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <HealthAlert
            label="Pending collaborations"
            value={health.requires_attention.pending_collaborations}
            color="amber"
          />
          <HealthAlert
            label="Payment failed"
            value={health.requires_attention.payment_failed}
          />
          <HealthAlert
            label="Pending invoices"
            value={health.requires_attention.pending_invoices}
            color="amber"
          />
          <HealthAlert
            label="Unread messages"
            value={health.requires_attention.unread_messages}
            color="amber"
          />
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={<FileText size={18} />}
          label="Total Offers"
          value={overview.offers_count || 0}
          sub={`${offers.created_last_7_days || 0} this week`}
          color="indigo"
        />
        <StatCard
          icon={<Users2 size={18} />}
          label="Collaborations"
          value={overview.collaborations_count || 0}
          sub={`${collaborations.created_last_7_days || 0} this week`}
          color="violet"
        />
        <StatCard
          icon={<DollarSign size={18} />}
          label="Commissions"
          value={overview.commissions_count || 0}
          sub={`${commissions.total_amount || 0} MAD total`}
          color="emerald"
        />
        <StatCard
          icon={<FileText size={18} />}
          label="Invoices"
          value={overview.invoices_count || 0}
          sub={`${invoices.total_amount || 0} MAD total`}
          color="cyan"
        />
      </div>

      {/* Finance Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={<Wallet size={18} />}
          label="Wallet Balance"
          value={`${finance.wallet_balance_total || 0} MAD`}
          sub={`${finance.active_wallets_count || 0} active wallets`}
          color="emerald"
        />
        <StatCard
          icon={<TrendingUp size={18} />}
          label="Transfers"
          value={finance.transfers_count || 0}
          sub={`${finance.transfers_total_amount || 0} MAD total`}
          color="indigo"
        />
        <StatCard
          icon={<CheckCircle2 size={18} />}
          label="Paid Commissions"
          value={`${finance.paid_commissions_total || 0} MAD`}
          color="emerald"
        />
        <StatCard
          icon={<MessageSquare size={18} />}
          label="Conversations"
          value={overview.conversations_count || 0}
          sub={`${overview.unread_messages || 0} unread`}
          color="violet"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Collaboration Status Pie */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 dark:text-slate-300">Collaborations by Status</h3>
          {collaborationStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={collaborationStatusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {collaborationStatusData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8 dark:text-slate-500">No collaboration data</p>
          )}
        </div>

        {/* Offers by Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 dark:text-slate-300">Offers by Status</h3>
          {offers.by_status ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={Object.entries(offers.by_status).map(([key, val]) => ({
                  name: key,
                  value: val,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#4f46e5" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8 dark:text-slate-500">No offers data</p>
          )}
        </div>
      </div>

      {/* Daily Activity */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 dark:text-slate-300">Daily Activity</h3>
        {dailyActivityData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyActivityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="offers" stroke="#4f46e5" strokeWidth={2} />
              <Line type="monotone" dataKey="collaborations" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="messages" stroke="#f59e0b" strokeWidth={2} />
              <Line type="monotone" dataKey="transfers" stroke="#8b5cf6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-400 text-center py-8 dark:text-slate-500">No activity data</p>
        )}
      </div>

      {/* Collaboration Rates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-700 mb-2 dark:text-slate-300">Collaboration Active Rate</h3>
          <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
            {health.rates?.collaboration_active_rate || 0}%
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-700 mb-2 dark:text-slate-300">Payment Success Rate</h3>
          <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
            {health.rates?.commission_payment_success_rate || 0}%
          </p>
        </div>
      </div>
    </div>
  );
}
